import React, { useCallback } from "react";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useSetMultiStake } from "@/features/betslip/hooks/useStakeActions";
import { useIsPermittedToBet } from "@/features/betslip/hooks/useIsPermittedToBet";
import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import { computeSelectionAlert } from "@/features/betslip/utils/alerts";
import { InlineAlert } from "@/features/betslip/components/parts/InlineAlert";
import type { Alert as AlertType } from "@/features/betslip/types";

type MultiLegAlertProps = {
  outcomeId: string;
};

/**
 * Per-leg alert for the multi card, rendered below the leg it concerns. Scoped
 * to odds changes, market status, permissions and multiLegRejections — it
 * deliberately ignores selectionResults and the held-bet overlay so that a leg
 * which is also placed as a single can't leak that single's accepted/rejected
 * result into the multi. Reads the effective market status so a SEM leg
 * resolves its status from its own legs.
 */
export const MultiLegAlert = ({ outcomeId }: MultiLegAlertProps) => {
  const selection = useBetslipStore((s) => s.selections[outcomeId]);
  const pendingOdds = useBetslipStore((s) => s.oddsChanges[outcomeId]);
  const marketStatus = useBetslipStore(
    (s) => s.effectiveMarketStatuses[outcomeId],
  );
  // Same as the single card: no permission claim until the session settles.
  const isLiveViewOnly = useBetslipStore(
    (s) => s.liveViewOnly[outcomeId] && s.hasSession,
  );
  const legRejection = useBetslipStore((s) => s.multiLegRejections[outcomeId]);
  const acceptOddsChange = useBetslipStore((s) => s.acceptOddsChange);
  const clearMultiLegRejection = useBetslipStore(
    (s) => s.clearMultiLegRejection,
  );
  const setOverlay = useBetslipStore((s) => s.setOverlay);
  const setMultiStake = useSetMultiStake();
  const isLoggedIn = useIsLoggedIn();
  const isPermitted = useIsPermittedToBet(outcomeId);

  const handleCta = useCallback(
    (cta: NonNullable<AlertType["cta"]>) => {
      switch (cta.action) {
        case "acceptOdds":
          acceptOddsChange(outcomeId);
          break;
        case "deposit":
          setOverlay({ type: "deposit" });
          break;
        case "updateMultiStake": {
          // A leg has no stake of its own — the amount the server is
          // offering back is the multi's, so the leg's "Update" moves that.
          const payload = cta.payload as
            | { alternativeStake?: number }
            | undefined;
          if (payload?.alternativeStake !== undefined) {
            setMultiStake(payload.alternativeStake);
          } else {
            clearMultiLegRejection(outcomeId);
          }
          break;
        }
        case "dismissMultiLeg":
          clearMultiLegRejection(outcomeId);
          break;
      }
    },
    [
      outcomeId,
      acceptOddsChange,
      clearMultiLegRejection,
      setOverlay,
      setMultiStake,
    ],
  );

  const alert = selection
    ? computeSelectionAlert({
        pendingOdds,
        marketStatus,
        result: legRejection
          ? { status: "rejected", rejection: legRejection }
          : undefined,
        isIntercepted: false,
        isSubmitting: false,
        isExotic: false,
        isNotPermitted: isLoggedIn && !isPermitted,
        isLiveViewOnly,
        dismissAction: "dismissMultiLeg",
        stakeAction: "updateMultiStake",
      })
    : null;

  return <InlineAlert alert={alert} onCta={handleCta} />;
};
